import { useEffect, useRef, useState } from 'react';

/**
 * Seconds elapsed since `resetKey` last changed, ticking every second while `active` is true.
 * Used to prove a long-running backend call (e.g. deploy_website's ~2 minute Cloudflare wait) is
 * still progressing rather than hung — a static "Deploying website…" label with no visible change
 * for over a minute reads as a frozen/dead request even when the spinner is technically animating.
 */
export default function useElapsedSeconds(resetKey: string | undefined, active: boolean): number {
  const [seconds, setSeconds] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    setSeconds(0);
  }, [resetKey]);

  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(() => {
      setSeconds(Math.round((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [active, resetKey]);

  return seconds;
}

export function formatElapsed(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
}
